package dev.danko.roblox_craft_ai.shared.model

import kotlinx.serialization.Serializable

@Serializable
data class ProviderConfigDto(
    val id: String,
    val name: String,
    val configured: Boolean,
    val priority: Int,
    val maskedKey: String = "",
    val description: String = ""
)

@Serializable
data class SaveKeysRequest(
    val glmApiKey: String? = null,
    val geminiApiKey: String? = null,
    val tripoApiKey: String? = null,
    val meshyApiKey: String? = null,
    val clearGlmKey: Boolean = false,
    val clearGeminiKey: Boolean = false,
    val clearTripoKey: Boolean = false,
    val clearMeshyKey: Boolean = false
)

@Serializable
data class TestKeyRequest(
    val providerId: String,
    val apiKey: String
)

@Serializable
data class TestKeyResponse(
    val valid: Boolean,
    val message: String,
    val providerId: String = "",
    val modelName: String = "",
    val latencyMs: Long = 0L
)

@Serializable
data class UserSettingsDto(
    val defaultAssetType: AssetType = AssetType.LUAU_SCRIPT,
    val defaultProviderId: String? = null,
    val skipSyncConfirm: Boolean = false,
    val temperature: Double = 0.3,
    val maxTokens: Int = 4096,
    val glmApiKey: String? = null,
    val geminiApiKey: String? = null,
    val tripoApiKey: String? = null,
    val meshyApiKey: String? = null
)

@Serializable
enum class ProviderType {
    OPENAI_COMPATIBLE,
    OFFICIAL_GEMINI,
    OFFICIAL_GLM,
    OFFICIAL_SILICONFLOW,
    MOCK
}

@Serializable
data class ProviderKeyDto(
    val id: String,
    val providerId: String,
    val apiKey: String = "",
    val label: String = "Default",
    val baseUrl: String? = null,
    val createdAtMs: Long = 0L,
    val isEnabled: Boolean = true,
    val priority: Int = 0
)

@Serializable
data class UserSettingsDtoV2(
    val sessionTokenHash12: String = "",
    val createdAtMs: Long = 0L,
    val updatedAtMs: Long = 0L,
    val providerKeys: List<ProviderKeyDto> = emptyList(),
    val defaultModelId: String? = null,
    val defaultAssetType: AssetType = AssetType.LUAU_SCRIPT,
    val skipSyncConfirmations: Boolean = false,
    val themeMode: String = "dark",
    val userLocale: String = "ru",
    val pluginPort: Int = 9095,
    val pluginAutoConnect: Boolean = true,
    val robloxStudioInstallPath: String? = null
)

@Serializable
data class ProviderMetadataDto(
    val providerId: String,
    val providerType: ProviderType,
    val displayName: String,
    val iconEmoji: String,
    val docsUrl: String,
    val baseUrlDefault: String,
    val isBuiltIn: Boolean = true,
    val supportsModelsList: Boolean = true,
    val defaultModelId: String,
    val apiKeyPatternRegex: String = "",
    val isFreeTierAvailable: Boolean = false
)

@Serializable
data class UpsertProviderKeyRequest(
    val providerId: String,
    val label: String = "Default",
    val plaintextKey: String? = null,
    val baseUrl: String? = null,
    val isEnabled: Boolean = true,
    val priority: Int? = null
)

@Serializable
data class ReorderKeysRequest(
    val orderedKeyIds: List<String>
)

@Serializable
data class FullSettingsResponse(
    val userSettings: UserSettingsDtoV2,
    val providersMeta: List<ProviderMetadataDto>,
    val legacyCompat: Boolean = true
)

@Serializable
enum class TestKeyStatusV2 {
    VALID,
    INVALID,
    RATE_LIMIT,
    NETWORK_UNKNOWN,
    UNKNOWN
}

@Serializable
data class TestKeyResponseV2(
    val status: TestKeyStatusV2 = TestKeyStatusV2.UNKNOWN,
    val message: String = "",
    val providerId: String = "",
    val modelName: String? = null,
    val latencyMs: Long? = null
)

@Serializable
data class UpdatePreferencesRequest(
    val defaultModelId: String? = null,
    val defaultAssetType: AssetType? = null,
    val skipSyncConfirmations: Boolean? = null,
    val themeMode: String? = null,
    val userLocale: String? = null,
    val pluginPort: Int? = null,
    val pluginAutoConnect: Boolean? = null,
    val robloxStudioInstallPath: String? = null
)

@Serializable
data class AdhocTestKeyRequest(
    val providerId: String,
    val apiKeyPlaintext: String,
    val baseUrl: String? = null
)


@Serializable
data class ServerConfigDto(
    val keysMode: String = "ui",
    val authMode: String = "optional",
    /** "open" or "invite" (only admin-approved emails can sign in). */
    val accessMode: String = "open"
)
